import { Button, Dropdown, Icon, Menu, Tag, Badge, Select, Popover, message } from "antd";
import moment from "moment";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { connect, useDispatch } from "react-redux";
import * as documentsActions from "./actions";
import {
  CardContainer,
  Card,
  CardTitle,
  CardCount,
  CardIcon
} from './styles';
import { ArchiveDocumentModal } from "./Modals/ArchiveDocumentModal";
import { DocumentDetailsDrawer } from "./Drawers/DocumentDetailsDrawer";
import { convertToBRL } from "~/utils/formatters";
import { ModuleHeader } from '~/components/ModuleHeader';
import Search from '~/_shared/components/Search';
import FilterPillsBar from '~/_shared/components/FilterPillsBar';
import MonthInput from '~/_shared/components/MonthInput';
import RestoredFiltersNotice from '~/_shared/components/RestoredFiltersNotice';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import { SporadictDocumentUpload } from '~/_domains/calendarTasks/Drawers/SporadicDocumentUpload';
import { SporadicDocumentUploadInBatch } from '~/_domains/calendarTasks/Drawers/SporadicDocumentUploadInBatch';
import { PendingDocumentsDrawer } from './components/PendingDocumentsDrawer';
import { useAppContext } from "~/AppContext";
import DocumentsTable from './table';
import { FaCheck, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { getDocumentsPeriodRequest, isPeriodTooLong, getDefaultDocumentsFilters } from './helpers';

var Option = Select.Option;

export const getStatusTag = (file) => {
  let selectedStatus;
  let icon;

  if (moment(file.due_at).isBefore(moment(), 'dates') && !file.viewed_at) {
    selectedStatus = { name: "Vencido", color: "#ff0a02" };
  } else if (file.viewed_at) {
    selectedStatus = { name: "Baixado", color: "#03b14e" };
  } else {
    selectedStatus = { name: "Não Baixado", color: "#0071c0" };
  }

  if (file.status === "Pago") {
    icon = <Icon type="check" />;
  } else if (file.viewed_at) {
    icon = <Icon type="download" />;
  }

  return (
    <Tag color={selectedStatus.color} style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
      {icon || null}
      {selectedStatus.name}
    </Tag>
  );
};


var FILTER_BY_OPTIONS = [
  { value: 'created_at', label: 'Cadastro' },
  { value: 'competency_at', label: 'Competência' },
  { value: 'due_at', label: 'Vencimento' },
];

var STATUS_OPTIONS = [
  { value: 'all', label: 'Todos', color: '#1890ff' },
  { value: 'pending', label: 'Envio Pendente', color: '#ffc107' },
  { value: 'overdue', label: 'Vencidos', color: '#ff0a02' },
  { value: 'notDownloaded', label: 'Não Baixados', color: '#0071c0' },
  { value: 'downloaded', label: 'Baixados', color: '#03b14e' },
  { value: 'archived', label: 'Arquivados', color: '#f5222d' },
];

var getStatusOption = function(value) {
  return STATUS_OPTIONS.find(function(o) { return o.value === value; });
};

const Documents = (props) => {
  const [impersonateAccessLoading, setImpersonateAccessLoading] = useState(null);
  const [archiveModal, setArchiveModal] = useState({ isOpen: false, id: null });
  const [emailLogsModal, setEmailLogsModal] = useState({ isOpen: false, id: null });
  const [documentDrawer, setDocumentDrawer] = useState({ isOpen: false });
  const [documentsInBatchDrawer, setDocumentsInBatchDrawer] = useState({ isOpen: false });
  const [pendingDocumentsDrawer, setPendingDocumentsDrawer] = useState({ isOpen: false });
  const { setOnboardingTaskAsDone } = useAppContext();
  const dispatch = useDispatch();

  // Applied filters: kept across refreshes for a few minutes (per tenant/user/page).
  var persisted = usePersistedFilters('documents', getDefaultDocumentsFilters);
  var applied = persisted.filters;
  var setApplied = persisted.setFilters;
  var defaults = persisted.defaults;
  var cardFilter = applied.status;
  var searchFilter = (applied.search || '').toLowerCase();

  // Draft period fields in the popover; only take effect on "Filtrar".
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftFilterBy, setDraftFilterBy] = useState(applied.filterBy);
  const [draftFrom, setDraftFrom] = useState(applied.from);
  const [draftTo, setDraftTo] = useState(applied.to);

  var isPeriodDefault = applied.filterBy === defaults.filterBy && applied.from === defaults.from && applied.to === defaults.to;

  // Load the list for the applied period: on mount (restored or default) and whenever it changes.
  useEffect(function() {
    dispatch(documentsActions.loadDocumentsByFilter(
      Object.assign(getDocumentsPeriodRequest(applied), { showLoading: false })
    ));
  }, [applied.filterBy, applied.from, applied.to, dispatch]);

  var handleFiltersOpenChange = useCallback(function(open) {
    if (open) {
      setDraftFilterBy(applied.filterBy);
      setDraftFrom(applied.from);
      setDraftTo(applied.to);
    }
    setFiltersOpen(open);
  }, [applied.filterBy, applied.from, applied.to]);

  var handleApplyFilter = useCallback(function(overrides) {
    var from = (overrides && overrides.from) || draftFrom;
    var to = (overrides && overrides.to) || draftTo;
    if (!from || !to) return;
    if (to < from) {
      var swap = from;
      from = to;
      to = swap;
    }
    if (isPeriodTooLong(from, to)) {
      return message.warning('O período não pode ser maior que 1 ano.');
    }
    setApplied({ filterBy: draftFilterBy, from: from, to: to });
    setFiltersOpen(false);
  }, [draftFilterBy, draftFrom, draftTo, setApplied]);

  var handleResetPeriod = useCallback(function() {
    setApplied({ filterBy: defaults.filterBy, from: defaults.from, to: defaults.to });
  }, [defaults, setApplied]);

  var setCardFilter = useCallback(function(status) {
    setApplied({ status: status });
  }, [setApplied]);

  var resetFilters = persisted.resetFilters;
  var handleClearFilters = useCallback(function() {
    resetFilters();
    setFiltersOpen(false);
  }, [resetFilters]);

  const impersonateAccess = useCallback((file) => {
    setImpersonateAccessLoading(file.id);
    const data = {
      customer_id: file.customer.id,
    };

    props.impersonateAccess(file.id, data)
      .then((response) => {
        const apiToken = response.payload.data.data.api_token;

        const customerAppLink = `http://${
          process.env.REACT_APP_CUSTOMER_DOMAIN
        }/?file=${file.id}&token=${apiToken}`;

        const newWindow = window.open(customerAppLink, '_blank');
        if (newWindow) {
          newWindow.focus();
        }
        setOnboardingTaskAsDone('y5z6a7');
      })
      .finally(() => {
        setImpersonateAccessLoading(null);
      })
  }, [props, setOnboardingTaskAsDone]);

  const generateAccessToFile = useCallback(async (file) => {
    try {
      const response = await props.generateAccessToFile(file.id);
      const { file_url: url } = response.payload.data;

      const whatsappMessage = `Prezado cliente, ${
        file.customer.name
      }.%0aEncontra-se disponível a obrigação ${
        file.description
      } com os seguintes dados:%0a%0aDescrição: ${
        file.description
      }%0aApuração / Data de competência: ${moment(file.competency_at).format(
        'MM/YYYY'
      )}%0aVencimento: ${moment(file.due_at).format(
        'DD/MM/YYYY'
      )}%0aValor: ${convertToBRL(file.amount)}%0aLink: ${url}`;

      const whatsappUrl = `https://api.whatsapp.com/send?phone=55${file.customer.phone}&text=${whatsappMessage}`;

      window.open(whatsappUrl, '_blank').focus();

    } catch (error) {
      console.log(error);
    }
  }, [props]);

  const getRevisedTitle = useCallback((file) => {
    const { revised_at, revised_by } = file;
    if (revised_at && revised_by) {
      const user = props.getUser(revised_by);

      if (!user) {
        return `Revisado as ${moment(revised_at).format('DD/MM/YYYY H:mm:ss')}`;
      }

      return `Revisado por ${user.name} - ${moment(revised_at).format('DD/MM/YYYY H:mm:ss')}`;
    }
    return 'Pendente';
  }, [props]);

  const openArchiveModal = useCallback((id) => {
    setArchiveModal({ isOpen: true, id });
  }, []);

  const closeArchiveModal = useCallback(() => {
    setArchiveModal({ isOpen: false, id: null });
  }, []);

  const openEmailLogsModal = useCallback((id) => {
    setEmailLogsModal({ isOpen: true, id });
  }, []);

  const closeEmailLogsModal = useCallback(() => {
    setEmailLogsModal({ isOpen: false, id: null });
  }, []);

  const openDocumentDrawer = useCallback(() => {
    setDocumentDrawer({ isOpen: true });
  }, []);

  const closeDocumentDrawer = useCallback(() => {
    setDocumentDrawer({ isOpen: false });
  }, []);

  const openDocumentsInBatchDrawer = useCallback(() => {
    setDocumentsInBatchDrawer({ isOpen: true });
  }, []);

  const closeDocumentsInBatchDrawer = useCallback(() => {
    setDocumentsInBatchDrawer({ isOpen: false });
  }, []);

  const openPendingDocumentsDrawer = useCallback(() => {
    setPendingDocumentsDrawer({ isOpen: true });
  }, []);

  const closePendingDocumentsDrawer = useCallback(() => {
    setPendingDocumentsDrawer({ isOpen: false });
  }, []);

  const { documents, isLoading } = props;

  const filteredDocuments = useMemo(() => {
    if (!documents || !Array.isArray(documents)) return [];
    return documents.filter((document) => {
      const customerName = document.customer.name.toLowerCase();
      const customerCod = document.customer.cod;

      if (
        customerName.includes(searchFilter) ||
        customerCod.includes(searchFilter)
      ) {
        switch (cardFilter) {
          case 'pending': {
            return document.delivery_methods.length === 0 && !document.archived_at && !document.archived_by;
          }
          case 'archived': {
            return !!document.archived_at && !!document.archived_by;
          }
          case 'overdue': {
            return moment(document.due_at).isBefore(moment(), 'dates') && !document.viewed_at && !document.archived_at && !document.archived_by;
          }
          case 'notDownloaded': {
            return !document.viewed_at && !moment(document.due_at).isBefore(moment(), 'dates') && !document.archived_at && !document.archived_by;
          }
          case 'downloaded': {
            return !!document.viewed_at && !document.archived_at && !document.archived_by;
          }
          default: {
            return !document.archived_at && !document.archived_by;
          }
        }
      }

      return false;
    });
  }, [documents, searchFilter, cardFilter]);

  const uniqueTaskNames = useMemo(() => {
    if (!documents || !Array.isArray(documents)) return [];
    return [
      ...new Set(
        documents
          .filter((document) => !!document.task_name)
          .map((document) => document.task_name))
    ];
  }, [documents]);

  const downloadedCount = useMemo(() => {
    if (!documents || !Array.isArray(documents)) return 0;
    return documents.filter((doc) => doc.viewed_at && !doc.archived_at && !doc.archived_by).length;
  }, [documents]);

  const notDownloadedCount = useMemo(() => {
    if (!documents || !Array.isArray(documents)) return 0;
    return documents.filter((doc) => !doc.viewed_at && !moment(doc.due_at).isBefore(moment(), 'dates') && !doc.archived_at && !doc.archived_by).length;
  }, [documents]);

  const overdueCount = useMemo(() => {
    if (!documents || !Array.isArray(documents)) return 0;
    return documents.filter((doc) => moment(doc.due_at).isBefore(moment(), 'dates') && !doc.viewed_at && !doc.archived_at && !doc.archived_by).length;
  }, [documents]);

  const pendingCount = useMemo(() => {
    if (!documents || !Array.isArray(documents)) return 0;
    return documents.filter((doc) => doc.delivery_methods.length === 0 && !doc.archived_at && !doc.archived_by).length;
  }, [documents]);

  const archivedCount = useMemo(() => {
    if (!documents || !Array.isArray(documents)) return 0;
    return documents.filter((doc) => !!doc.archived_at && !!doc.archived_by).length;
  }, [documents]);

  const handleCardClick = useCallback((type) => {
    setApplied(function(prev) {
      return { search: '', status: prev.status === type ? 'all' : type };
    });
  }, [setApplied]);

  var statusCounts = {
    all: (documents && documents.length) || 0,
    pending: pendingCount,
    overdue: overdueCount,
    notDownloaded: notDownloadedCount,
    downloaded: downloadedCount,
    archived: archivedCount,
  };

  var filterByOption = FILTER_BY_OPTIONS.find(function(o) { return o.value === applied.filterBy; });
  var formatMonth = function(value) { return moment(value, 'YYYY-MM').format('MM/YYYY'); };
  var periodLabel = (filterByOption ? filterByOption.label : 'Período') + ': ' + formatMonth(applied.from) +
    (applied.to !== applied.from ? ' - ' + formatMonth(applied.to) : '');
  var statusOption = cardFilter !== 'all' ? getStatusOption(cardFilter) : null;

  var activeFilterCount = (isPeriodDefault ? 0 : 1) + (statusOption ? 1 : 0) + (applied.search ? 1 : 0);

  var pills = [{
    key: 'period',
    label: periodLabel,
    color: isPeriodDefault ? '#1890ff' : '#722ed1',
    icon: 'calendar',
    onRemove: isPeriodDefault ? undefined : handleResetPeriod,
  }];
  if (statusOption) {
    pills.push({
      key: 'status',
      label: statusOption.label,
      color: statusOption.color,
      onRemove: function() { setCardFilter('all'); },
    });
  }
  if (applied.search) {
    pills.push({
      key: 'search',
      label: 'Busca: ' + applied.search,
      color: '#722ed1',
      icon: 'search',
      onRemove: function() { setApplied({ search: '' }); },
    });
  }

  var restoredSummary = pills
    .filter(function(pill) { return pill.key !== 'period' || !isPeriodDefault; })
    .map(function(pill) { return pill.label; })
    .join(' · ');

  return (
    <div>
      <ModuleHeader
          breadcrumbs={['Itens Enviados','Documentos Enviados']}
          title="Documentos Enviados"
          search={
            <Search
              value={applied.search}
              onChange={function(e) { setApplied({ search: e.target.value }); }}
              placeholder="Pesquisar"
            />
          }
          center={
            <>
              <Popover
                trigger="click"
                placement="bottomRight"
                visible={filtersOpen}
                onVisibleChange={handleFiltersOpenChange}
                content={
                  <div style={{ width: 400 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                      <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>Filtros</span>
                      {!persisted.isDefault && (
                        <a style={{ fontSize: 12, color: '#f5222d' }} onClick={handleClearFilters}>
                          Limpar tudo
                        </a>
                      )}
                    </div>

                    {/* Status filter */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {STATUS_OPTIONS.map(function(option) {
                          var opt = Object.assign({ count: statusCounts[option.value] }, option);
                          var isActive = cardFilter === opt.value;
                          return (
                            <div
                              key={opt.value}
                              onClick={function() {
                                setCardFilter(isActive ? 'all' : opt.value);
                              }}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                                border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'),
                                background: isActive ? opt.color + '0a' : '#fafafa',
                                transition: 'all 0.2s',
                              }}
                            >
                              <span style={{ fontSize: 12, color: isActive ? '#262626' : '#8c8c8c' }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: opt.color, display: 'inline-block', marginRight: 8 }} />
                                {opt.label}
                              </span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: isActive ? opt.color : '#bfbfbf' }}>{opt.count}</span>
                                {isActive && <Icon type="check" style={{ color: opt.color, fontSize: 12 }} />}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Period filter */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Filtrar por</div>
                      <Select
                        value={draftFilterBy}
                        onChange={function(v) { setDraftFilterBy(v); }}
                        style={{ width: '100%', marginBottom: 8 }}
                        getPopupContainer={function(trigger) { return trigger.parentNode; }}
                      >
                        {FILTER_BY_OPTIONS.map(function(opt) {
                          return <Option key={opt.value} value={opt.value}>{opt.label}</Option>;
                        })}
                      </Select>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Período</div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <MonthInput
                          value={draftFrom}
                          onChange={function(v) { setDraftFrom(v); }}
                          onPressEnter={function(v) { handleApplyFilter({ from: v }); }}
                          style={{ flex: 1 }}
                        />
                        <span style={{ color: '#8c8c8c', lineHeight: '32px' }}>-</span>
                        <MonthInput
                          value={draftTo}
                          onChange={function(v) { setDraftTo(v); }}
                          onPressEnter={function(v) { handleApplyFilter({ to: v }); }}
                          style={{ flex: 1 }}
                        />
                      </div>
                      <div style={{ fontSize: 11, color: '#bfbfbf', marginTop: 4 }}>
                        Digite o mês (ex.: 03/2026) ou use o calendário. Enter aplica.
                      </div>
                    </div>

                    <Button type="primary" block onClick={function() { handleApplyFilter(); }}>
                      Filtrar
                    </Button>
                  </div>
                }
              >
                <Badge
                  count={activeFilterCount}
                  offset={[-4, 0]}
                  style={{ backgroundColor: '#722ed1' }}
                >
                  <Button icon="bars" style={{ borderRadius: 8 }}>Filtros</Button>
                </Badge>
              </Popover>

              <Dropdown
                trigger={['click']}
                overlay={
                  <Menu>
                    <Menu.Item key="1" onClick={openDocumentDrawer}>
                      <Icon type="file" style={{ marginRight: 8 }} />
                      Individual
                    </Menu.Item>
                    <Menu.Item key="2" onClick={openDocumentsInBatchDrawer}>
                      <Icon type="copy" style={{ marginRight: 8 }} />
                      Em lote
                    </Menu.Item>
                    <Menu.Item key="3" onClick={openPendingDocumentsDrawer}>
                      <Icon type="clock-circle" style={{ marginRight: 8 }} />
                      Pendentes
                    </Menu.Item>
                  </Menu>
                }
              >
                <Button icon="plus" shape="circle" />
              </Dropdown>
            </>
          }
          advanced={
            <FilterPillsBar
              pills={pills}
              onClearAll={persisted.isDefault ? undefined : handleClearFilters}
            />
          }
      />
      <RestoredFiltersNotice
        visible={persisted.wasRestored}
        summary={restoredSummary}
        onReset={handleClearFilters}
        onDismiss={persisted.dismissRestored}
      />
      {cardFilter !== 'archived' && (
      <CardContainer>
        <Card 
          background="#fff3cd" 
          onClick={() => handleCardClick('pending')}
          isActive={cardFilter === 'pending'}
        >
          <CardIcon>
            <FaExclamationTriangle size={24} color="#ffc107" />
          </CardIcon>
          <CardTitle>Envio Pendente</CardTitle>
          <CardCount>{pendingCount}</CardCount>
        </Card>
        <Card 
          background="#ffe6e6" 
          onClick={() => handleCardClick('overdue')}
          isActive={cardFilter === 'overdue'}
        >
          <CardIcon>
            <FaExclamationTriangle size={24} color="#ff0a02" />
          </CardIcon>
          <CardTitle>Vencidos</CardTitle>
          <CardCount>{overdueCount}</CardCount>
        </Card>
        <Card 
          background="#e6f0ff" 
          onClick={() => handleCardClick('notDownloaded')}
          isActive={cardFilter === 'notDownloaded'}
        >
          <CardIcon>
            <FaTimes size={24} color="#0071c0" />
          </CardIcon>
          <CardTitle>Não Baixados</CardTitle>
          <CardCount>{notDownloadedCount}</CardCount>
        </Card>
        <Card 
          background="#e6f7e6" 
          onClick={() => handleCardClick('downloaded')}
          isActive={cardFilter === 'downloaded'}
        >
          <CardIcon>
            <FaCheck size={24} color="#03b14e" />
          </CardIcon>
          <CardTitle>Baixados</CardTitle>
          <CardCount>{downloadedCount}</CardCount>
        </Card>
      </CardContainer>
      )}
      <DocumentsTable
        documents={filteredDocuments}
        isLoading={isLoading}
        impersonateAccess={impersonateAccess}
        openArchiveModal={openArchiveModal}
        openEmailLogsModal={openEmailLogsModal}
        generateAccessToFile={generateAccessToFile}
        getRevisedTitle={getRevisedTitle}
        visualizationType={cardFilter}
        uniqueTaskNames={uniqueTaskNames}
        setVisualizationType={setCardFilter}
        props={props}
        impersonateAccessLoading={impersonateAccessLoading}
        onOpenDetails={openEmailLogsModal}
      />
      <SporadictDocumentUpload
        isOpen={documentDrawer.isOpen}
        onClose={closeDocumentDrawer}
      />
      <SporadicDocumentUploadInBatch
        isVisible={documentsInBatchDrawer.isOpen}
        onClose={closeDocumentsInBatchDrawer}
      />
      <PendingDocumentsDrawer
        isVisible={pendingDocumentsDrawer.isOpen}
        onClose={closePendingDocumentsDrawer}
      />
      <ArchiveDocumentModal
        isOpen={archiveModal.isOpen}
        id={archiveModal.id}
        closeModal={closeArchiveModal}
        archiveDocument={props.archiveDocument}
      />
      <DocumentDetailsDrawer
        isOpen={emailLogsModal.isOpen}
        documentId={emailLogsModal.id}
        document={(documents || []).find(function(d) { return d.id === emailLogsModal.id; })}
        closeModal={closeEmailLogsModal}
        onViewAsCustomer={impersonateAccess}
        viewAsCustomerLoading={!!emailLogsModal.id && impersonateAccessLoading === emailLogsModal.id}
        onSendWhatsapp={generateAccessToFile}
        onSendEmails={props.sendEmails}
        onArchive={openArchiveModal}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    documents: state.documents.list,
    isLoading: state.documents.isLoading,
    user: state.user,
    getUser: (id) => state.users.find((user) => user.id === id)
  };
};

const mapDispatchProps = (dispatch) => {
  return {
    impersonateAccess: (id, data) => dispatch(documentsActions.impersonateAccess(id, data)),
    changeRevised: (id, data) => dispatch(documentsActions.changeRevised(id, data)),
    generateAccessToFile: (id) => dispatch(documentsActions.generateAccessToFile(id)),
    archiveDocument: (id, data) => dispatch(documentsActions.archiveDocument(id, data)),
    sendEmails: (id) => dispatch(documentsActions.sendEmails(id))
  };
};

export default connect(mapStateToProps, mapDispatchProps)(Documents);
